import {
  type SurveyProductionRateAssumptionsDto,
  type SurveyProductionRateDto,
  SurveyProductionRatePrimaryConstraint,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ModalSteps, type StepConfig, useSteps } from '@components/shared/modal-steps.component';
import { useAppForm } from '@components/ui/form/form';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useInnovatorUpdateSurveyProductionRate } from '@queries/survey.query';
import {
  MRL_PRODUCTION_RATE_QUALIFIER_MAX_LENGTH,
  mrlProductionRateAssumptionsSchema,
  mrlProductionRateDetailsSchema,
  mrlProductionRateSchema,
} from '@schemas/mrl-production-rate.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler, resetFieldMeta } from '@utils/form.util';
import type { FC, ReactNode } from 'react';

type MrlProductionRateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  productionRate?: SurveyProductionRateDto | null;
  surveyId: string;
};

type AssumptionKey = keyof SurveyProductionRateAssumptionsDto;

type AssumptionFieldNameMap = {
  criticalMaterials: {
    inPlace: 'assumptions.criticalMaterials.inPlace';
    qualifier: 'assumptions.criticalMaterials.qualifier';
  };
  facilityTooling: {
    inPlace: 'assumptions.facilityTooling.inPlace';
    qualifier: 'assumptions.facilityTooling.qualifier';
  };
  funding: {
    inPlace: 'assumptions.funding.inPlace';
    qualifier: 'assumptions.funding.qualifier';
  };
  regulatory: {
    inPlace: 'assumptions.regulatory.inPlace';
    qualifier: 'assumptions.regulatory.qualifier';
  };
  supplyChain: {
    inPlace: 'assumptions.supplyChain.inPlace';
    qualifier: 'assumptions.supplyChain.qualifier';
  };
  workforce: {
    inPlace: 'assumptions.workforce.inPlace';
    qualifier: 'assumptions.workforce.qualifier';
  };
};

type DetailsStepFieldRoot = 'fullRateProduction' | 'lowRateProduction' | 'surgeProduction';

const isDetailsStepFieldRoot = (value: unknown): value is DetailsStepFieldRoot =>
  value === 'fullRateProduction' || value === 'lowRateProduction' || value === 'surgeProduction';

enum Steps {
  PRODUCTION_DETAILS = 0,
  ASSUMPTIONS = 1,
}

const stepData: StepConfig[] = [
  {
    label: 'Production Details',
    step: Steps.PRODUCTION_DETAILS,
  },
  {
    label: 'Assumptions',
    step: Steps.ASSUMPTIONS,
  },
];

const stepSchemaMap = {
  [Steps.ASSUMPTIONS]: mrlProductionRateAssumptionsSchema,
  [Steps.PRODUCTION_DETAILS]: mrlProductionRateDetailsSchema,
};

const primaryConstraintOptions = [
  { label: 'Materials', value: SurveyProductionRatePrimaryConstraint.Materials },
  { label: 'Tooling', value: SurveyProductionRatePrimaryConstraint.Tooling },
  { label: 'Workforce', value: SurveyProductionRatePrimaryConstraint.Workforce },
  { label: 'Facility', value: SurveyProductionRatePrimaryConstraint.Facility },
  { label: 'Sub-tier', value: SurveyProductionRatePrimaryConstraint.SubTier },
  { label: 'Funding', value: SurveyProductionRatePrimaryConstraint.Funding },
];

const assumptionLabels: Record<AssumptionKey, string> = {
  criticalMaterials: 'Critical materials - availability confirmed (alloys, semiconductors, energetics, rare earths)',
  facilityTooling: 'Facility and tooling - no new capex required',
  funding: 'Funding - no CR, stop-work, or de-obligation assumed',
  regulatory: 'Regulatory - ITAR/EAR/export licenses and permits current',
  supplyChain: 'Supply chain - all tiers identified, no single-source risk',
  workforce: 'Workforce - skilled and cleared to required level',
};

const assumptionFieldNames: AssumptionFieldNameMap = {
  criticalMaterials: {
    inPlace: 'assumptions.criticalMaterials.inPlace',
    qualifier: 'assumptions.criticalMaterials.qualifier',
  },
  facilityTooling: {
    inPlace: 'assumptions.facilityTooling.inPlace',
    qualifier: 'assumptions.facilityTooling.qualifier',
  },
  funding: {
    inPlace: 'assumptions.funding.inPlace',
    qualifier: 'assumptions.funding.qualifier',
  },
  regulatory: {
    inPlace: 'assumptions.regulatory.inPlace',
    qualifier: 'assumptions.regulatory.qualifier',
  },
  supplyChain: {
    inPlace: 'assumptions.supplyChain.inPlace',
    qualifier: 'assumptions.supplyChain.qualifier',
  },
  workforce: {
    inPlace: 'assumptions.workforce.inPlace',
    qualifier: 'assumptions.workforce.qualifier',
  },
};

const assumptionsDescription =
  'For each assumption, indicate whether it is in place. If not, provide a one-line qualifier.';

const fullRateProductionDescription =
  'What sustained production rate can you commit to, and what is the lead time from order to delivery at that rate?';

const lowRateProductionDescription =
  'What is your current (or most recently demonstrated) production rate, and how long have you sustained it?';

const surgeProductionDescription =
  'What is your maximum surge rate, how long to ramp to it, and what is the primary constraint on going higher?';

const getDefaultValues = (productionRate?: SurveyProductionRateDto | null) => ({
  assumptions: {
    criticalMaterials: {
      inPlace: productionRate?.assumptions.criticalMaterials.inPlace ?? true,
      qualifier: productionRate?.assumptions.criticalMaterials.qualifier ?? '',
    },
    facilityTooling: {
      inPlace: productionRate?.assumptions.facilityTooling.inPlace ?? true,
      qualifier: productionRate?.assumptions.facilityTooling.qualifier ?? '',
    },
    funding: {
      inPlace: productionRate?.assumptions.funding.inPlace ?? true,
      qualifier: productionRate?.assumptions.funding.qualifier ?? '',
    },
    regulatory: {
      inPlace: productionRate?.assumptions.regulatory.inPlace ?? true,
      qualifier: productionRate?.assumptions.regulatory.qualifier ?? '',
    },
    supplyChain: {
      inPlace: productionRate?.assumptions.supplyChain.inPlace ?? true,
      qualifier: productionRate?.assumptions.supplyChain.qualifier ?? '',
    },
    workforce: {
      inPlace: productionRate?.assumptions.workforce.inPlace ?? true,
      qualifier: productionRate?.assumptions.workforce.qualifier ?? '',
    },
  },
  fullRateProduction: {
    evidence: productionRate?.fullRateProduction.evidence ?? '',
    leadTimeWeeks: productionRate?.fullRateProduction.leadTimeWeeks ?? null,
    rateUnitsPerMonth: productionRate?.fullRateProduction.rateUnitsPerMonth ?? null,
  },
  lowRateProduction: {
    durationMonths: productionRate?.lowRateProduction.durationMonths ?? null,
    evidence: productionRate?.lowRateProduction.evidence ?? '',
    rateUnitsPerMonth: productionRate?.lowRateProduction.rateUnitsPerMonth ?? null,
  },
  surgeProduction: {
    evidence: productionRate?.surgeProduction.evidence ?? '',
    peakRateUnitsPerMonth: productionRate?.surgeProduction.peakRateUnitsPerMonth ?? null,
    primaryConstraint: productionRate?.surgeProduction.primaryConstraint ?? null,
    rampTimeWeeks: productionRate?.surgeProduction.rampTimeWeeks ?? null,
  },
});

const FormSection: FC<{ children: ReactNode; description: string; title: string }> = (props) => {
  const { children, description, title } = props;

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
        <p className="text-sm leading-5 text-gray-500">{description}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
};

export const MrlProductionRateModal: FC<MrlProductionRateModalProps> = (props) => {
  const { isOpen, onClose, productionRate, surveyId } = props;
  const { isPending, mutateAsync: updateProductionRate } = useInnovatorUpdateSurveyProductionRate();

  const { handleStepClick, resetSteps, setStep, step, stepsInfo, updateStepState } = useSteps(
    stepData,
    Steps.PRODUCTION_DETAILS,
    Boolean(productionRate),
  );

  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues: getDefaultValues(productionRate),
    onSubmit: async ({ value }) => {
      if (step < Steps.ASSUMPTIONS) {
        updateStepState(step);
        return;
      }

      const validationResult = mrlProductionRateSchema.safeParse(value);
      if (!validationResult.success) {
        const firstIssueRoot = validationResult.error.issues[0]?.path[0];
        if (isDetailsStepFieldRoot(firstIssueRoot)) {
          setStep(Steps.PRODUCTION_DETAILS);
        }
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      await updateProductionRate({
        productionRate: validationResult.data,
        surveyId,
      });

      successToast({ description: 'Production rate saved.' });
      onCloseOverride();
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: ({ formApi }) => {
        const currentStepSchema = stepSchemaMap[step];
        const errors = formApi.parseValuesWithSchema(currentStepSchema);
        if (errors) return errors;
      },
    },
  });

  const assumptionValues = useStore(form.store, (state) => state.values.assumptions);

  const resetForm = (): void => {
    form.reset();
    resetSteps();
  };

  const onCloseOverride = (): void => {
    resetForm();
    onClose();
  };

  const renderYesNoField = (key: AssumptionKey) => {
    const fieldNames = assumptionFieldNames[key];

    return (
      <form.AppField name={fieldNames.inPlace}>
        {(field) => {
          const fieldId = key;
          const selection = field.state.value ? 'yes' : 'no';

          return (
            <RadioGroup
              className="flex flex-row gap-4"
              value={selection}
              onValueChange={(option) => {
                const nextValue = option === 'yes';
                field.handleChange(nextValue);

                if (nextValue) {
                  form.setFieldMeta(fieldNames.qualifier, resetFieldMeta);
                  form.setFieldValue(fieldNames.qualifier, '');
                }
              }}
            >
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <RadioGroupItem
                  id={`${fieldId}-yes`}
                  value="yes"
                />
                <label
                  className="cursor-pointer"
                  htmlFor={`${fieldId}-yes`}
                >
                  Yes
                </label>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <RadioGroupItem
                  id={`${fieldId}-no`}
                  value="no"
                />
                <label
                  className="cursor-pointer"
                  htmlFor={`${fieldId}-no`}
                >
                  No
                </label>
              </div>
            </RadioGroup>
          );
        }}
      </form.AppField>
    );
  };

  const renderAssumptionField = (key: AssumptionKey) => {
    const shouldShowQualifier = assumptionValues[key].inPlace === false;

    return (
      <div
        className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3"
        key={key}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-medium text-gray-700">{assumptionLabels[key]}</div>
          {renderYesNoField(key)}
        </div>
        {shouldShowQualifier && (
          <form.AppField name={`assumptions.${key}.qualifier`}>
            {(field) => (
              <field.TextField
                label="Qualifier"
                maxLength={MRL_PRODUCTION_RATE_QUALIFIER_MAX_LENGTH}
                placeholder="Short qualifier"
                required
              />
            )}
          </form.AppField>
        )}
      </div>
    );
  };

  return (
    <Modal
      footer={{
        rightButton: {
          children: step === Steps.ASSUMPTIONS ? 'Save' : 'Continue',
          loading: isPending,
          onClick: () => form.handleSubmit(),
        },
      }}
      header={{ title: productionRate ? 'Edit Production Rate' : 'Add Production Rate' }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="760px"
    >
      <div className="flex flex-col gap-4">
        <ModalSteps
          onStepClick={handleStepClick}
          stepsInfo={stepsInfo}
        />

        <form.AppForm>
          {step === Steps.PRODUCTION_DETAILS && (
            <div className="flex flex-col gap-4">
              <FormSection
                description={lowRateProductionDescription}
                title="Low Rate Production"
              >
                <form.AppField name="lowRateProduction.rateUnitsPerMonth">
                  {(field) => (
                    <field.TextField
                      label="Rate (units/month)"
                      min={0}
                      placeholder="Enter units per month"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="lowRateProduction.durationMonths">
                  {(field) => (
                    <field.TextField
                      label="Duration sustained (months)"
                      min={0}
                      placeholder="Enter months sustained"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="lowRateProduction.evidence">
                  {(field) => (
                    <field.TextField
                      className="sm:col-span-2"
                      label="Evidence"
                      placeholder="Document ID or URL"
                      required
                    />
                  )}
                </form.AppField>
              </FormSection>

              <FormSection
                description={fullRateProductionDescription}
                title="Full Rate Production"
              >
                <form.AppField name="fullRateProduction.rateUnitsPerMonth">
                  {(field) => (
                    <field.TextField
                      label="Rate (units/month)"
                      min={0}
                      placeholder="Enter committed units per month"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="fullRateProduction.leadTimeWeeks">
                  {(field) => (
                    <field.TextField
                      label="Order-to-delivery lead time (weeks)"
                      min={0}
                      placeholder="Enter lead time in weeks"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="fullRateProduction.evidence">
                  {(field) => (
                    <field.TextField
                      className="sm:col-span-2"
                      label="Evidence"
                      placeholder="Document ID or URL"
                      required
                    />
                  )}
                </form.AppField>
              </FormSection>

              <FormSection
                description={surgeProductionDescription}
                title="Surge Production"
              >
                <form.AppField name="surgeProduction.peakRateUnitsPerMonth">
                  {(field) => (
                    <field.TextField
                      label="Peak rate (units/month)"
                      min={0}
                      placeholder="Enter peak units per month"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="surgeProduction.rampTimeWeeks">
                  {(field) => (
                    <field.TextField
                      label="Ramp time from FRP (weeks)"
                      min={0}
                      placeholder="Enter ramp time in weeks"
                      required
                      type="number"
                    />
                  )}
                </form.AppField>
                <form.AppField name="surgeProduction.primaryConstraint">
                  {(field) => (
                    <field.SelectField
                      label="Primary constraint"
                      options={primaryConstraintOptions}
                      placeholder="Select primary constraint"
                      required
                    />
                  )}
                </form.AppField>
                <form.AppField name="surgeProduction.evidence">
                  {(field) => (
                    <field.TextField
                      label="Evidence or basis"
                      placeholder="Document ID or URL"
                      required
                    />
                  )}
                </form.AppField>
              </FormSection>
            </div>
          )}

          {step === Steps.ASSUMPTIONS && (
            <div className="flex flex-col gap-3">
              <p className="text-sm leading-5 text-gray-500">{assumptionsDescription}</p>
              {renderAssumptionField('supplyChain')}
              {renderAssumptionField('criticalMaterials')}
              {renderAssumptionField('workforce')}
              {renderAssumptionField('facilityTooling')}
              {renderAssumptionField('funding')}
              {renderAssumptionField('regulatory')}
            </div>
          )}
        </form.AppForm>
      </div>
    </Modal>
  );
};
